import { stringify } from "neoqs";
import { api } from "@/api/api";
import { routes } from "@/api/routes";
import type {
  APIAppleNewsSection,
  APIArticle,
  APIJournalist,
  ArticleCollection,
  ArticleImagesStatus,
  ArticleLinkVerdict,
  ArticleTag,
  ArticleTopic,
  CollabKey,
  CreateMediaItemResponse,
  GoogleDoc,
  GridCollection,
  GridImage,
  GridImageListResponse,
  MediaItem,
  NewApiArticle,
  PaginatedResponse,
  PlatformOptions,
  PuzzleLookupResult,
  PuzzleType,
  TokenType,
  UserProfile,
  VideoListResponse,
  WoodwingBook,
} from "@/api/types";
import { stringToColor } from "@/utils/colors";
import type { CropRect } from "@/utils/crop";
import { filenameFromDisposition } from "@/utils/download-file";

export const loginByCode = async (code: string) => {
  const resp = await api.post<{ token: TokenType; user: UserProfile }>(routes.auth.loginByCode, {
    code,
  });
  resp.data.user.color = stringToColor(String(resp.data.user.id));
  resp.data.user.roles = resp.data.user.roles ?? [];

  return resp.data;
};

export const refreshToken = async (refreshToken: string) => {
  const resp = await api.post<{ token: TokenType; user: UserProfile }>(routes.auth.refreshToken, {
    refresh_token: refreshToken,
  });

  resp.data.user.color = stringToColor(String(resp.data.user.id));
  resp.data.user.roles = resp.data.user.roles ?? [];

  return resp.data;
};

export const getUserProfile = async (token: string, signal?: AbortSignal) => {
  const resp = await api.post<UserProfile>(routes.auth.profile, undefined, {
    signal,
    headers: { Authorization: `Bearer ${token}` },
  });

  resp.data.color = stringToColor(String(resp.data.id));
  resp.data.roles = resp.data.roles ?? [];

  return { user: resp.data, token: null };
};

export type Timetypes = "today" | "tomorrow" | "coming-week" | "coming-sunday" | "specific-dates";

export type GetArticlesParams = {
  page?: number;
  query?: string;
  limit?: number;
  timetype?: Timetypes;
  publish_at_after?: string;
  publish_at_before?: string;
  platform?: PlatformOptions;
  status?: string | string[];
  section?: number[];
  id?: number;
  // Comma-separated, "-" prefix for descending. The viewset accepts publish_at, title, id and
  // created_at, and defaults to "-created_at" when this is omitted.
  ordering?: string;
};

export const getArticles = async (params: GetArticlesParams, signal: AbortSignal) => {
  const resp = await api.get<PaginatedResponse<APIArticle>>(
    `${routes.articles.list}${stringify(params, { addQueryPrefix: true, arrayFormat: "repeat" })}`,
    { signal },
  );

  return resp.data;
};

export const ARTICLES_MAX_PAGE_SIZE = 50;
export const MAX_CALENDAR_PAGES = 6;
/** The most stories one calendar week can show; past this the board reports `truncated`. */
export const CALENDAR_WEEK_STORY_CAP = MAX_CALENDAR_PAGES * ARTICLES_MAX_PAGE_SIZE;

/**
 * Collects every article matching `params`, up to a ceiling. A week of the calendar routinely
 * runs past the endpoint's 50-row page cap — a single Sunday paper is around 45 stories on its
 * own — so the board has to walk the pages. Deliberately not a bigger page: each list request
 * also fans out to three synchronous Wagtail lookups, so a fatter page makes the slowest request
 * slower instead of making fewer of them. `truncated` reports that the ceiling was reached.
 */
export const getAllArticles = async (params: GetArticlesParams, signal: AbortSignal) => {
  const results: APIArticle[] = [];

  for (let page = 1; page <= MAX_CALENDAR_PAGES; page += 1) {
    const resp = await getArticles({ ...params, page, limit: ARTICLES_MAX_PAGE_SIZE }, signal);
    results.push(...resp.results);
    if (!resp.next) return { results, truncated: false };
  }

  return { results, truncated: true };
};

export const getOldArticles = async (params: GetArticlesParams, signal: AbortSignal) => {
  const resp = await api.get<PaginatedResponse<APIArticle>>(
    `${routes.articles.wagtailList}${stringify(params, { addQueryPrefix: true, arrayFormat: "repeat" })}`,
    { signal },
  );

  return resp.data;
};

/**
 * Ask the CMS whether an internal article link will publish. Backed by the publish preflight's
 * own resolver, so the warning an editor sees while writing matches the gate that runs on
 * publish. Deliberately NOT `getOldArticles`: that endpoint is filtered to DigiFirst-authored
 * articles, so using it as an existence check reported every pre-Wagtail-2 article as missing
 * (OBS-1670).
 */
export const resolveArticleLink = async (slug: string) => {
  const resp = await api.get<ArticleLinkVerdict>(
    `${routes.articles.resolveLink}${stringify({ slug }, { addQueryPrefix: true })}`,
  );

  return resp.data;
};

export const createArticle = async () => {
  const resp = await api.post<NewApiArticle>(routes.articles.create);
  return resp.data;
};

export type getMediaItemsParams = {
  id?: Array<number> | null;
  ordering?: "created_at" | "-created_at";
  page?: number;
  query?: string;
  limit?: number;
};

export const getMediaItems = async (
  params: getMediaItemsParams,
  signal: AbortSignal,
): Promise<PaginatedResponse<MediaItem>> => {
  const resp = await api.get<PaginatedResponse<MediaItem>>(
    `${routes.media.list}${stringify(params, { addQueryPrefix: true, arrayFormat: "repeat" })}`,
    { signal },
  );

  return resp.data;
};

export type createMediaItemParams = {
  title: string;
  file: File;
  caption?: string;
  credit?: string;
  alt_text?: string;
  description?: string;
  /**
   * The editor's crop box, as fractions of the frame. Applied server-side: a
   * browser-side crop re-encodes the file and strips the EXIF/IPTC that cms
   * reads credit, description and tags from.
   */
  crop?: { x: number; y: number; w: number; h: number };
};

export const createMediaItem = async (params: createMediaItemParams): Promise<CreateMediaItemResponse> => {
  const formData = new FormData();
  formData.append("title", params.title);
  formData.append("file", params.file as Blob);
  if (params.caption) formData.append("caption", params.caption);
  if (params.credit) formData.append("credit", params.credit);
  if (params.alt_text) formData.append("alt_text", params.alt_text);
  if (params.description) formData.append("description", params.description);
  if (params.crop) formData.append("crop", JSON.stringify(params.crop));

  const resp = await api.post<CreateMediaItemResponse>(routes.media.create, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return resp.data;
};

export const uploadEmbedAsset = async (file: File): Promise<{ url: string }> => {
  const formData = new FormData();
  formData.append("file", file);
  const resp = await api.post<{ url: string }>(routes.embedAssets.upload, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120 * 1000,
  });
  return resp.data;
};

export type getJournalistsParams = {
  id?: Array<number>;
  page?: number;
  query?: string;
  limit?: number;
};

export const createJournalist = async (data: { name: string; about?: string }): Promise<APIJournalist> => {
  const resp = await api.post<APIJournalist>(routes.journalists.create, data, { timeout: 30000 });
  return resp.data;
};

export const getJournalists = async (
  params: getJournalistsParams,
  signal: AbortSignal,
): Promise<PaginatedResponse<APIJournalist>> => {
  const resp = await api.get<PaginatedResponse<APIJournalist>>(
    `${routes.journalists.list}${stringify(params, { addQueryPrefix: true, arrayFormat: "repeat" })}`,
    {
      signal,
      timeout: 50000,
    },
  );

  return resp.data;
};

export type getTagsParams = {
  id?: Array<number>;
  page?: number;
  query?: string;
  limit?: number;
};

export const getTags = async (
  params: getTagsParams,
  signal: AbortSignal,
): Promise<PaginatedResponse<ArticleTag>> => {
  const resp = await api.get<PaginatedResponse<ArticleTag>>(
    `${routes.articles.tags}${stringify(params, { addQueryPrefix: true, arrayFormat: "repeat" })}`,
    {
      signal,
      timeout: 50000,
    },
  );

  return resp.data;
};

export type getTopicsParams = {
  id?: Array<number>;
  page?: number;
  query?: string;
  limit?: number;
};

export const getTopics = async (
  params: getTopicsParams,
  signal: AbortSignal,
): Promise<PaginatedResponse<ArticleTopic>> => {
  const resp = await api.get<PaginatedResponse<ArticleTopic>>(
    `${routes.articles.topics}${stringify(params, { addQueryPrefix: true, arrayFormat: "repeat" })}`,
    {
      signal,
      timeout: 50000,
    },
  );

  return resp.data;
};

export const getAppleNewsSections = async (signal: AbortSignal) => {
  const resp = await api.get<APIAppleNewsSection[]>(routes.articles.appleNewsSections, { signal });

  return resp.data?.map((section) => ({
    id: section.section_id,
    name: section.name,
    is_default: section.is_default,
  }));
};

export type getCollectionsParams = {
  id?: Array<number>;
  page?: number;
  query?: string;
  limit?: number;
};

export const getCollections = async (
  params: getCollectionsParams,
  signal: AbortSignal,
): Promise<PaginatedResponse<ArticleCollection>> => {
  const resp = await api.get<PaginatedResponse<ArticleCollection>>(
    `${routes.articles.collections}${stringify(params, { addQueryPrefix: true, arrayFormat: "repeat" })}`,
    {
      signal,
      timeout: 50000,
    },
  );

  return resp.data;
};

export interface GetRecentGoogleDocsParams {
  query?: string;
}

export const getRecentGoogleDocs = async (params: GetRecentGoogleDocsParams, signal?: AbortSignal) => {
  const resp = await api.get<GoogleDoc[]>(routes.google.listDocs, {
    params: {
      query: params.query,
    },
    signal,
  });

  return resp.data;
};

export interface GetGoogleDocContentParams {
  google_file_id: string;
}

export const getGoogleDocContent = async (params: GetGoogleDocContentParams) => {
  const resp = await api.get<{
    document: { type: "doc"; content: object[] };
    tags?: ArticleTag["id"][];
    topic?: ArticleTopic["id"] | null;
    kicker?: string | null;
    standfirst?: string | null;
    summary?: string | null;
    catchline?: string | null;
  }>(routes.google.contents(params.google_file_id), { timeout: 60 * 1000 });

  return resp.data;
};

export interface ImportGoogleDocParams {
  google_doc_id: string;
}

export const importGoogleDoc = async (params: ImportGoogleDocParams) => {
  // 30s ceiling matches the server's gunicorn worker timeout. The async import
  // returns in ~1s; this just avoids a false client timeout if the synchronous
  // setup (Google fetch + Tiptap writes) is briefly slow.
  const resp = await api.post<{ id: number }>(routes.google.importDoc(params.google_doc_id), undefined, {
    timeout: 30 * 1000,
  });

  return resp.data;
};

export interface RecipeIngredient {
  section_list_name?: string | null;
  name: string;
  value: string;
  unit?: string | null;
}

export interface RecipeStep {
  section_list_name?: string | null;
  text: string;
}

export interface RecipeData {
  id: number;
  slug: string;
  title: string;
  image?: number | null;
  standfirst?: string | null;
  datePublished?: string | null;
  prep_time_mins?: number | null;
  cook_time_mins?: number | null;
  total_time_mins?: number | null;
  recipe_yield?: string | null;
  recipe_category?: string | null;
  recipe_cuisine?: string | null;
  keywords?: string | null;
  author?: string | null;
  ingredients: { name: string; itemListElement: RecipeIngredient[] }[];
  steps: { name: string; itemListElement: RecipeStep[] }[];
}

export interface RecipeUpdatePayload {
  prep_time_mins?: number;
  cook_time_mins?: number;
  total_time_mins?: number;
  recipe_yield?: string;
  keywords?: string;
  image?: number | null;
  ingredients?: RecipeIngredient[];
  steps?: RecipeStep[];
}

export const getRecipe = async (id: number): Promise<RecipeData> => {
  const resp = await api.get<RecipeData>(routes.recipes.detail(id));
  return resp.data;
};

export const updateRecipe = async (id: number, data: RecipeUpdatePayload): Promise<RecipeData> => {
  const resp = await api.patch<RecipeData>(routes.recipes.detail(id), data);
  return resp.data;
};

export const archiveArticle = async (articleId: string | number) => {
  await api.post(routes.articles.archive(articleId), undefined, { timeout: 60 * 1000 });
};

export const deleteArticle = async (articleId: string | number) => {
  await api.delete(routes.articles.delete(articleId));
};

export const unpublishArticle = async (articleId: string | number) => {
  await api.post(routes.articles.unpublish(articleId), undefined, { timeout: 60 * 1000 });
};

export const republishArticle = async (articleId: string | number) => {
  await api.post(routes.articles.republish(articleId), undefined, { timeout: 60 * 1000 });
};

export const getArticleImagesStatus = async (articleId: string | number) => {
  const resp = await api.post<ArticleImagesStatus>(routes.articles.imagesStatus(articleId));
  return resp.data;
};

export const purgeArticleCache = async (articleId: string | number) => {
  await api.post(routes.articles.purgeCache(articleId), undefined, { timeout: 60 * 1000 });
};

export const cancelPublish = async (articleId: string | number) => {
  await api.post(routes.articles.cancelPublish(articleId));
};

export const getCollabKey = async (articleId: string | number): Promise<CollabKey> => {
  const resp = await api.post<CollabKey>(routes.articles.collabKey(articleId));
  return resp.data;
};

export const scheduleArticle = async (params: {
  articleId: string | number;
  data: {
    publish_at: string;
  };
}) => {
  await api.post(routes.articles.schedule(params.articleId), params.data, { timeout: 60 * 1000 });
};

export const backToDraftArticle = async (articleId: string | number) => {
  await api.post(routes.articles.backToDraft(articleId), undefined, { timeout: 60 * 1000 });
};

export const getWoodwingBooks = async (signal: AbortSignal) => {
  const resp = await api.get<WoodwingBook[]>(routes.woodwing.books, {
    signal,
  });

  return resp.data;
};

export const sendToPrint = async ({
  articleId,
  markCopyDone,
}: {
  articleId: string;
  markCopyDone?: boolean;
}): Promise<void> => {
  // OBS-1578: this call is the sum of two server-side budgets, not one request.
  // digifirst-cms spends up to WOODWING_PUBLIC_JSON_TIMEOUT_SECONDS (raised 25 -> 45)
  // fetching the WoodWing JSON from cms, THEN up to WOODWING_CALL_TIMEOUT_SECONDS (30)
  // on the print bridge — a worst case of ~76s. At the previous 60s this timeout fired
  // first, turning a slow-but-successful send into a browser-side failure that
  // digifirst-cms had already logged as a success. Revert both together once the
  // underlying latency (OBS-1590) is fixed; this is a mitigation, not a target.
  await api.post(
    routes.woodwing.sendToPrint(articleId),
    markCopyDone ? { mark_copy_done: true } : undefined,
    { timeout: 120 * 1000 },
  );
};

export const archivePrint = async (articleId: string): Promise<void> => {
  await api.post(routes.woodwing.archive(articleId), undefined, { timeout: 60 * 1000 });
};

export const schedulePrint = async (params: {
  articleId: string | number;
  data: {
    edition_date: string;
    book: string;
    section: string;
  };
}) => {
  await api.post(routes.woodwing.schedule(params.articleId), params.data, { timeout: 60 * 1000 });
};

export const getPuzzleTypes = async (signal: AbortSignal): Promise<PuzzleType[]> => {
  const resp = await api.get<PuzzleType[]>(routes.puzzles.types, { signal });
  return resp.data;
};

export type PresenceUser = {
  user_id: number;
  display_name: string;
};

export const postPresence = async (articleId: number | string): Promise<void> => {
  await api.post(routes.presence.article(articleId));
};

export const deletePresence = async (articleId: number | string): Promise<void> => {
  await api.delete(routes.presence.article(articleId));
};

export const getPresenceBulk = async (articleIds: number[]): Promise<Record<string, PresenceUser[]>> => {
  const resp = await api.get<Record<string, PresenceUser[]>>(routes.presence.bulk, {
    params: { article_ids: articleIds.join(",") },
  });
  return resp.data;
};

export const getPuzzleLookup = async (
  params: { variant: string; number: string },
  signal: AbortSignal,
): Promise<PuzzleLookupResult> => {
  const resp = await api.get<PuzzleLookupResult>(
    `${routes.puzzles.lookup}${stringify(params, { addQueryPrefix: true })}`,
    { signal },
  );
  return resp.data;
};

export const getYoutubeVideos = async (
  pageToken: string | undefined,
  signal: AbortSignal,
): Promise<VideoListResponse> => {
  const params = pageToken ? `?page_token=${encodeURIComponent(pageToken)}` : "";
  const resp = await api.get<VideoListResponse>(`${routes.youtube.videos}${params}`, { signal });
  return resp.data;
};

export const getGridCollections = async (signal: AbortSignal): Promise<GridCollection> => {
  const resp = await api.get<GridCollection>(routes.grid.collections, { signal });
  return resp.data;
};

export const getGridImages = async (
  params: { q?: string; collection?: string; offset?: number; length?: number },
  signal: AbortSignal,
): Promise<GridImageListResponse> => {
  const resp = await api.get<GridImageListResponse>(
    `${routes.grid.images}${stringify(params, { addQueryPrefix: true })}`,
    { signal },
  );
  return resp.data;
};

export const getGridImage = async (imageId: string, signal: AbortSignal): Promise<GridImage> => {
  const resp = await api.get<GridImage>(routes.grid.image(imageId), { signal });
  return resp.data;
};

/**
 * The stored copy of a library image — the crop the article actually ran with,
 * since crops are baked into the bytes and the coordinates are not kept.
 * Proxied through digifirst-cms: media sits on another origin with no CORS.
 */
export const downloadStoredImage = async (mediaId: number): Promise<{ blob: Blob; filename: string }> => {
  const resp = await api.get<Blob>(routes.media.download(mediaId), {
    responseType: "blob",
    timeout: 120_000,
  });
  return {
    blob: resp.data,
    filename: filenameFromDisposition(resp.headers["content-disposition"]) ?? `image-${mediaId}.jpg`,
  };
};

export type UpdateMediaItemParams = {
  id: number;
  credit?: string;
  caption?: string;
};

/** Correct an image already in the library: who it is credited to, and its caption. */
export const updateMediaItem = async ({ id, ...changes }: UpdateMediaItemParams): Promise<MediaItem> => {
  const resp = await api.patch<MediaItem>(routes.media.detail(id), changes);
  return resp.data;
};

export type ImportGridImagePayload = {
  image_id: string;
  /** The editor's crop box, as fractions of the frame. Omitted for a full frame. */
  crop?: { x: number; y: number; w: number; h: number };
  title?: string;
  description?: string;
  /** The editor's own caption, where Grid has none to offer. */
  caption?: string;
  credit?: string;
  byline?: string;
  supplier_reference?: string;
  original_filename?: string;
};

export const downloadGridImage = async (
  imageId: string,
  crop?: CropRect,
): Promise<{ blob: Blob; filename: string }> => {
  const resp = await api.get<Blob>(routes.grid.download(imageId), {
    params: crop ? { crop: JSON.stringify(crop) } : undefined,
    responseType: "blob",
    // The client default is tuned for JSON; a full-resolution original is ~10MB
    // and arrives via our proxy, so it needs considerably longer.
    timeout: 120_000,
  });
  return {
    blob: resp.data,
    filename: filenameFromDisposition(resp.headers["content-disposition"]) ?? `${imageId}.jpg`,
  };
};

export const importGridImage = async (payload: ImportGridImagePayload): Promise<MediaItem> => {
  const resp = await api.post<MediaItem>(routes.grid.import, payload);
  return resp.data;
};
